import React from 'react';

interface CardProps {
    children: React.ReactNode;
    className?: string;
    variant?: 'default' | 'elevated' | 'outlined' | 'filled';
    size?: 'sm' | 'md' | 'lg';
    padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
    rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
    shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
    border?: boolean;
    background?: 'white' | 'gray' | 'blue' | 'green' | 'red' | 'yellow' | 'transparent';
    onClick?: () => void;
    disabled?: boolean;
    hover?: boolean;
}

export default function Card({
    children,
    className = '',
    variant = 'default',
    size = 'md',
    padding = 'md',
    rounded = 'md',
    shadow = 'md',
    border = false,
    background = 'white',
    onClick,
    disabled = false,
    hover = true,
}: CardProps) {
    const baseClasses = 'transition-all duration-200';

    const variantClasses = {
        default: 'bg-white border border-gray-200',
        elevated: 'bg-white',
        outlined: 'bg-transparent border-2 border-gray-300',
        filled: 'border-none',
    };

    const sizeClasses = {
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg',
    };

    const paddingClasses = {
        none: 'p-0',
        sm: 'p-2',
        md: 'p-4',
        lg: 'p-6',
        xl: 'p-8',
    };

    const roundedClasses = {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        full: 'rounded-full',
    };

    const shadowClasses = {
        none: 'shadow-none',
        sm: 'shadow-sm',
        md: 'shadow-md',
        lg: 'shadow-lg',
        xl: 'shadow-xl',
    };

    const backgroundClasses = {
        white: 'bg-white',
        gray: 'bg-gray-100',
        blue: 'bg-blue-50',
        green: 'bg-green-50',
        red: 'bg-red-50',
        yellow: 'bg-yellow-50',
        transparent: 'bg-transparent',
    };

    const hoverClasses = hover && onClick && !disabled
        ? 'hover:shadow-lg hover:scale-[1.02] cursor-pointer'
        : '';

    const disabledClasses = disabled
        ? 'opacity-50 cursor-not-allowed'
        : '';

    const borderClasses = border
        ? 'border border-gray-200'
        : '';

    const combinedClasses = [
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        paddingClasses[padding],
        roundedClasses[rounded],
        shadowClasses[shadow],
        backgroundClasses[background],
        borderClasses,
        hoverClasses,
        disabledClasses,
        className,
    ].filter(Boolean).join(' ');

    const handleClick = () => {
        if (onClick && !disabled) {
            onClick();
        }
    };

    return (
        <div
            className={combinedClasses}
            onClick={handleClick}
            {...(onClick && { role: 'button' })}
            tabIndex={onClick && !disabled ? 0 : undefined}
            onKeyDown={(e) => {
                if (onClick && !disabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onClick();
                }
            }}
            {...(onClick && { 'aria-label': 'Clickable card' })}
        >
            {children}
        </div>
    );
}
